import React from 'react';
import {
  Box,
  Typography,
  Paper,
  Divider,
  Card,
  CardContent,
} from '@mui/material';
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
} from 'recharts';
import { CostBreakdown, FinancialSummary as FinancialSummaryType } from '../types/budget';
import { formatCurrency } from '../utils/calculations';

interface DataVisualizationProps {
  costBreakdown: CostBreakdown;
  financialSummary: FinancialSummaryType;
}

const COLORS = [
  '#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8',
  '#82CA9D', '#FFC658', '#FF7C7C', '#8DD1E1', '#D084D0'
];

export const DataVisualization: React.FC<DataVisualizationProps> = ({
  costBreakdown,
  financialSummary,
}) => {
  // Prepare data for pie chart
  const pieChartData = Object.entries(costBreakdown)
    .filter(([_, items]) => items.length > 0)
    .map(([category, items]) => ({
      name: category.charAt(0).toUpperCase() + category.slice(1).replace(/([A-Z])/g, ' $1'),
      value: items.reduce((sum: number, item: any) => sum + item.totalValue, 0),
      items: items.length,
    }))
    .filter(item => item.value > 0);

  // Prepare data for bar chart
  const barChartData = Object.entries(costBreakdown)
    .filter(([_, items]) => items.length > 0)
    .map(([category, items]) => ({
      category: category.charAt(0).toUpperCase() + category.slice(1).replace(/([A-Z])/g, ' $1'),
      totalCost: items.reduce((sum: number, item: any) => sum + item.totalValue, 0),
      itemCount: items.length,
      avgCost: items.length > 0 ? items.reduce((sum: number, item: any) => sum + item.totalValue, 0) / items.length : 0,
    }))
    .filter(item => item.totalCost > 0);

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <Card sx={{ p: 1, bgcolor: 'background.paper' }}>
          <Typography variant="body2">
            {label || payload[0].name}
          </Typography>
          {payload.map((entry: any, index: number) => (
            <Typography key={index} variant="body2" color={entry.color}>
              {entry.name}: {formatCurrency(entry.value)}
            </Typography>
          ))}
        </Card>
      );
    }
    return null;
  };

  const renderCustomizedLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent }: any) => {
    const RADIAN = Math.PI / 180;
    const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);

    if (percent < 0.05) return null; // Don't show label for small slices

    return (
      <text
        x={x}
        y={y}
        fill="white"
        textAnchor={x > cx ? 'start' : 'end'}
        dominantBaseline="central"
        fontSize="12"
        fontWeight="bold"
      >
        {`${(percent * 100).toFixed(0)}%`}
      </text>
    );
  };

  return (
    <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
      <Typography variant="h5" gutterBottom>
        Cost Analysis & Visualization
      </Typography>
      <Divider sx={{ mb: 3 }} />

      {pieChartData.length > 0 ? (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' }, gap: 3 }}>
          {/* Pie Chart */}
          <Card variant="outlined">
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Cost Distribution by Category
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={pieChartData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={renderCustomizedLabel}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {pieChartData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip content={<CustomTooltip />} />
                </PieChart>
              </ResponsiveContainer>
              <Box sx={{ mt: 2 }}>
                {pieChartData.map((entry, index) => (
                  <Box key={entry.name} sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <Box
                      sx={{
                        width: 12,
                        height: 12,
                        backgroundColor: COLORS[index % COLORS.length],
                        mr: 1,
                        borderRadius: '2px',
                      }}
                    />
                    <Typography variant="body2" sx={{ flexGrow: 1 }}>
                      {entry.name} ({entry.items} items)
                    </Typography>
                    <Typography variant="body2" fontWeight="bold">
                      {formatCurrency(entry.value)}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </CardContent>
          </Card>

          {/* Bar Chart */}
          <Card variant="outlined">
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Cost Comparison by Category
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={barChartData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis 
                    dataKey="category" 
                    angle={-45}
                    textAnchor="end"
                    height={80}
                    interval={0}
                    tick={{ fontSize: 10 }}
                  />
                  <YAxis tick={{ fontSize: 10 }} />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="totalCost" fill="#8884d8" />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Box>
      ) : (
        <Card variant="outlined">
          <CardContent>
            <Typography variant="h6" align="center" color="text.secondary">
              No cost data available for visualization
            </Typography>
            <Typography variant="body2" align="center" color="text.secondary">
              Add cost items to see visual analysis
            </Typography>
          </CardContent>
        </Card>
      )}

      {/* Summary Statistics */}
      <Box sx={{ mt: 3 }}>
        <Card variant="outlined" sx={{ bgcolor: 'grey.50' }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Cost Analysis Summary
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr 1fr' }, gap: 2 }}>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Total Cost Categories
                </Typography>
                <Typography variant="h6" fontWeight="bold">
                  {pieChartData.length}
                </Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Highest Cost Category
                </Typography>
                <Typography variant="h6" fontWeight="bold">
                  {pieChartData.length > 0 
                    ? pieChartData.reduce((max, item) => item.value > max.value ? item : max).name
                    : 'N/A'
                  }
                </Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Cost Margin
                </Typography>
                <Typography 
                  variant="h6" 
                  fontWeight="bold"
                  color={financialSummary.profitPercentage >= 0 ? 'success.main' : 'error.main'}
                >
                  {financialSummary.profitPercentage.toFixed(1)}%
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Box>
    </Paper>
  );
};
